import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Modal,
  Spinner,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { camerasApi } from "@/api";

const CAMERA_POSITIONS = [
  { value: "regular", label: "Regular" },
  { value: "high", label: "High" },
  { value: "angle", label: "Angle" },
  { value: "behind_gates", label: "Behind Gates" },
];

// ─── Manufacturers Tab ──────────────────────────────────────────────

function ManufacturersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await camerasApi.getManufacturers();
      setItems(Array.isArray(data) ? data : data?.results ?? []);
    } catch {
      toast.error("Failed to load manufacturers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await camerasApi.updateManufacturer(editing.id, { name: name.trim() });
        toast.success("Manufacturer updated");
      } else {
        await camerasApi.createManufacturer({ name: name.trim() });
        toast.success("Manufacturer created");
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(
        err?.response?.data?.name?.[0] ||
          err?.response?.data?.detail ||
          "Failed to save manufacturer"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete manufacturer "${row.name}"?`)) return;
    try {
      await camerasApi.deleteManufacturer(row.id);
      toast.success("Manufacturer deleted");
      fetchItems();
    } catch {
      toast.error("Failed to delete manufacturer");
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 60 },
      { accessorKey: "name", header: "Name" },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        cell: ({ row }) => (
          <div className="tw-flex tw-gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => openEdit(row.original)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <h5 className="tw-mb-0">Manufacturers</h5>
        <Button size="sm" onClick={openCreate}>
          Add Manufacturer
        </Button>
      </div>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover size="sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} style={{ width: h.getSize() }}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={3} className="tw-text-center">
                  No manufacturers
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? "Edit Manufacturer" : "Add Manufacturer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hikvision"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ─── Camera Models Tab ──────────────────────────────────────────────

function CameraModelsTab() {
  const [types, setTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formModel, setFormModel] = useState("");
  const [formManufacturer, setFormManufacturer] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [typeData, mfrData] = await Promise.all([
        camerasApi.getCameraTypes(),
        camerasApi.getManufacturers(),
      ]);
      setTypes(Array.isArray(typeData) ? typeData : typeData?.results ?? []);
      setManufacturers(
        Array.isArray(mfrData) ? mfrData : mfrData?.results ?? []
      );
    } catch {
      toast.error("Failed to load camera models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setFormModel("");
    setFormManufacturer("");
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormModel(row.model);
    setFormManufacturer(row.manufacturer?.id ?? "");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formModel.trim() || !formManufacturer) return;
    setSaving(true);
    try {
      const payload = {
        model: formModel.trim(),
        manufacturer: Number(formManufacturer),
      };
      if (editing) {
        await camerasApi.updateCameraType(editing.id, payload);
        toast.success("Camera model updated");
      } else {
        await camerasApi.createCameraType(payload);
        toast.success("Camera model created");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(
        err?.response?.data?.model?.[0] ||
          err?.response?.data?.manufacturer?.[0] ||
          err?.response?.data?.detail ||
          "Failed to save camera model"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const label = `${row.manufacturer?.name ?? ""} ${row.model}`.trim();
    if (!window.confirm(`Delete camera model "${label}"?`)) return;
    try {
      await camerasApi.deleteCameraType(row.id);
      toast.success("Camera model deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete camera model");
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 60 },
      {
        id: "manufacturer",
        header: "Manufacturer",
        accessorFn: (row) => row.manufacturer?.name ?? "—",
      },
      { accessorKey: "model", header: "Model" },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        cell: ({ row }) => (
          <div className="tw-flex tw-gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => openEdit(row.original)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: types,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <h5 className="tw-mb-0">Camera Models</h5>
        <Button size="sm" onClick={openCreate}>
          Add Camera Model
        </Button>
      </div>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover size="sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} style={{ width: h.getSize() }}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {types.length === 0 && (
              <tr>
                <td colSpan={4} className="tw-text-center">
                  No camera models
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? "Edit Camera Model" : "Add Camera Model"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-flex tw-flex-col tw-gap-3">
          <Form.Group>
            <Form.Label>Manufacturer</Form.Label>
            <Form.Select
              value={formManufacturer}
              onChange={(e) => setFormManufacturer(e.target.value)}
            >
              <option value="">-- Select Manufacturer --</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Model</Form.Label>
            <Form.Control
              value={formModel}
              onChange={(e) => setFormModel(e.target.value)}
              placeholder="e.g. DS-2CD2T47G2-L"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formModel.trim() || !formManufacturer}
          >
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ─── Cameras Tab ─────────────────────────────────────────────────────

const EMPTY_CAMERA = {
  name: "",
  arena: "",
  camera_type: "",
  camera_position: "regular",
  camera_http_address: "",
  camera_rtsp_address: "",
  camera_user: "",
  camera_password: "",
  is_private: false,
};

function CamerasTab() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [cameraTypes, setCameraTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CAMERA });
  const [saving, setSaving] = useState(false);
  const [sorting, setSorting] = useState([]);

  // Arena filter (set of selected arena ids)
  const [selectedArenas, setSelectedArenas] = useState(new Set());

  // Snapshot state
  const [snapshotLoading, setSnapshotLoading] = useState(null);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [snapshotCameraName, setSnapshotCameraName] = useState("");

  const arenas = useMemo(() => {
    const map = new Map();
    cameras.forEach((c) => {
      const key = c.arena ?? "none";
      const label = c.arena_name ?? "No Arena";
      map.set(key, label);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [cameras]);

  // Group camera types by manufacturer for <optgroup>
  const typesByManufacturer = useMemo(() => {
    const groups = {};
    cameraTypes.forEach((ct) => {
      const mfr = ct.manufacturer?.name ?? "Other";
      if (!groups[mfr]) groups[mfr] = [];
      groups[mfr].push(ct);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [cameraTypes]);

  const filteredCameras = useMemo(() => {
    if (selectedArenas.size === 0) return cameras;
    return cameras.filter((c) => selectedArenas.has(c.arena ?? "none"));
  }, [cameras, selectedArenas]);

  const toggleArena = (arenaId) => {
    setSelectedArenas((prev) => {
      const next = new Set(prev);
      if (next.has(arenaId)) next.delete(arenaId);
      else next.add(arenaId);
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [camData, typeData] = await Promise.all([
        camerasApi.getCameras(),
        camerasApi.getCameraTypes(),
      ]);
      setCameras(
        Array.isArray(camData) ? camData : camData?.results ?? []
      );
      setCameraTypes(
        Array.isArray(typeData) ? typeData : typeData?.results ?? []
      );
    } catch {
      toast.error("Failed to load cameras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_CAMERA });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      arena: row.arena ?? "",
      camera_type: row.camera_type ?? "",
      camera_position: row.camera_position || "regular",
      camera_http_address: row.camera_http_address || "",
      camera_rtsp_address: row.camera_rtsp_address || "",
      camera_user: row.camera_user || "",
      camera_password: row.camera_password || "",
      is_private: row.is_private ?? false,
    });
    setShowModal(true);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        arena: form.arena || null,
        camera_type: form.camera_type || null,
        camera_http_address: form.camera_http_address || null,
        camera_rtsp_address: form.camera_rtsp_address || null,
        camera_user: form.camera_user || null,
        camera_password: form.camera_password || null,
      };
      if (editing) {
        await camerasApi.updateCamera(editing.id, payload);
        toast.success("Camera updated");
      } else {
        await camerasApi.createCamera(payload);
        toast.success("Camera created");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "object"
          ? JSON.stringify(err.response.data)
          : "Failed to save camera");
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete camera "${row.name}"?`)) return;
    try {
      await camerasApi.deleteCamera(row.id);
      toast.success("Camera deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete camera");
    }
  };

  const handleSnapshot = async (row) => {
    if (!row.camera_rtsp_address) {
      toast.error("This camera has no RTSP address configured");
      return;
    }
    setSnapshotLoading(row.id);
    setSnapshotCameraName(row.name || `Camera #${row.id}`);
    try {
      const blob = await camerasApi.getSnapshot(row.id);
      const url = URL.createObjectURL(blob);
      setSnapshotUrl(url);
      setShowSnapshotModal(true);
    } catch (err) {
      const data = err?.response?.data;
      let msg = "Failed to get snapshot";
      if (data instanceof Blob) {
        try { msg = JSON.parse(await data.text()).detail; } catch {}
      } else if (data?.detail) {
        msg = data.detail;
      }
      toast.error(msg);
    } finally {
      setSnapshotLoading(null);
    }
  };

  const closeSnapshot = () => {
    setShowSnapshotModal(false);
    if (snapshotUrl) {
      URL.revokeObjectURL(snapshotUrl);
      setSnapshotUrl(null);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 60 },
      { accessorKey: "arena_name", header: "Arena" },
      { accessorKey: "name", header: "Name" },
      { accessorKey: "camera_type_name", header: "Camera Type" },
      {
        accessorKey: "is_private",
        header: "Private",
        cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
        size: 80,
      },
      {
        id: "actions",
        header: "Actions",
        size: 330,
        cell: ({ row }) => (
          <div className="tw-flex tw-gap-2">
            <Button
              size="sm"
              variant="outline-info"
              onClick={() => navigate(`/panel/playground-field/${row.original.id}`)}
            >
              Field
            </Button>
            <Button
              size="sm"
              variant="outline-success"
              disabled={!row.original.camera_rtsp_address || snapshotLoading !== null}
              onClick={() => handleSnapshot(row.original)}
            >
              {snapshotLoading === row.original.id ? <Spinner size="sm" /> : "Snapshot"}
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => openEdit(row.original)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [snapshotLoading]
  );

  const table = useReactTable({
    data: filteredCameras,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <h5 className="tw-mb-0">Cameras</h5>
        <Button size="sm" onClick={openCreate}>
          Add Camera
        </Button>
      </div>

      {arenas.length > 1 && (
        <div className="tw-flex tw-gap-2 tw-flex-wrap tw-mb-3">
          <span className="tw-text-sm tw-self-center tw-text-gray-500">Arena:</span>
          {arenas.map((a) => (
            <Button
              key={a.id}
              size="sm"
              variant={selectedArenas.has(a.id) ? "primary" : "outline-secondary"}
              onClick={() => toggleArena(a.id)}
            >
              {a.name}
            </Button>
          ))}
          {selectedArenas.size > 0 && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setSelectedArenas(new Set())}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover size="sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} style={{ width: h.getSize() }}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {cameras.length === 0 && (
              <tr>
                <td colSpan={7} className="tw-text-center">
                  No cameras
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? "Edit Camera" : "Add Camera"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-flex tw-flex-col tw-gap-3">
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Camera name"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Arena</Form.Label>
            <Form.Select
              value={form.arena}
              onChange={(e) => handleChange("arena", e.target.value)}
            >
              <option value="">-- No Arena --</option>
              {arenas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Camera Type</Form.Label>
            <Form.Select
              value={form.camera_type}
              onChange={(e) => handleChange("camera_type", e.target.value)}
            >
              <option value="">-- No Type --</option>
              {typesByManufacturer.map(([mfr, items]) => (
                <optgroup key={mfr} label={mfr}>
                  {items.map((ct) => (
                    <option key={ct.id} value={ct.id}>
                      {ct.model}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Camera Position</Form.Label>
            <Form.Select
              value={form.camera_position}
              onChange={(e) => handleChange("camera_position", e.target.value)}
            >
              {CAMERA_POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>HTTP Address</Form.Label>
            <Form.Control
              value={form.camera_http_address}
              onChange={(e) =>
                handleChange("camera_http_address", e.target.value)
              }
              placeholder="https://..."
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>RTSP Address</Form.Label>
            <Form.Control
              value={form.camera_rtsp_address}
              onChange={(e) =>
                handleChange("camera_rtsp_address", e.target.value)
              }
              placeholder="rtsp://..."
            />
          </Form.Group>

          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <Form.Group>
              <Form.Label>Camera User</Form.Label>
              <Form.Control
                value={form.camera_user}
                onChange={(e) => handleChange("camera_user", e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Camera Password</Form.Label>
              <Form.Control
                type="password"
                value={form.camera_password}
                onChange={(e) =>
                  handleChange("camera_password", e.target.value)
                }
              />
            </Form.Group>
          </div>

          <Form.Check
            type="checkbox"
            label="Private"
            checked={form.is_private}
            onChange={(e) => handleChange("is_private", e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Snapshot modal */}
      <Modal
        show={showSnapshotModal}
        onHide={closeSnapshot}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Snapshot — {snapshotCameraName}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, backgroundColor: "#000" }}>
          {snapshotUrl && (
            <img
              src={snapshotUrl}
              alt="Camera snapshot"
              style={{ width: "100%", display: "block" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

const Cameras = () => {
  return (
    <div className="tw-p-4">
      <h3 className="tw-mb-4">Cameras</h3>
      <Tabs defaultActiveKey="cameras" className="tw-mb-3">
        <Tab eventKey="cameras" title="Cameras">
          <CamerasTab />
        </Tab>
        <Tab eventKey="models" title="Camera Models">
          <CameraModelsTab />
        </Tab>
        <Tab eventKey="manufacturers" title="Manufacturers">
          <ManufacturersTab />
        </Tab>
      </Tabs>
    </div>
  );
};

export default Cameras;
